import { HeaderValidation } from '../../elements/HeaderValidation';
import { SelectChangeEvent, Stack, Typography } from '@mui/material';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useInjection } from './hook';
import { PagingInfo } from 'types/paging';
import { TableCustom } from 'app/components/elements/TableCustom';
import { AssetIssues, IOptions, SubmittedPhoto, TypePhotoSubmit } from 'types/room';
import { HEADER_ASSET_VALIDATION_TAB } from '../../../const';
import {
  ContentExtraTableValidation,
  FormEditSubmittedEntries
} from '../../elements/ContentExtraTableValidation';
import { useNavigate } from 'react-router-dom';
import {
  renderDataComplianceIssue,
  renderRoomLevelIssues
} from 'app/components/RoomPage/tableContent';
import { HEADER_REPORT_PREVIEW, ROOM_LEVEL_ISSUES } from 'const/room';
import { DialogConfirm } from 'app/components/elements/DialogConfirm';
import { ModalGenerateReport } from 'app/components/RoomPage/modalGenReport';
import { AssetValidationExpandedContent } from './AssetValidationExpandedContent';
import { lowerCase, startCase, uniqBy } from 'lodash';
import { formatName } from 'utils/convertData';
import { DatePicker } from '../../../../../components/elements/DatePicker';
import dayjs from 'dayjs';
import { ButtonCustom } from '../../../../../components/elements/ButtonCustom';
import { ListOptions } from 'app/pages/RoomReportViewPhotoPage/hook';
import { ExportColumnModal } from './ExportColumnModal';
import { InputSelect } from 'app/components/elements/InputSelect';
import MaintainenceFilter from '../../../../../components/elements/MaintenanceFilter';
import { useAuth } from 'contexts/auth';
import { Role } from 'const/common';
import { CloseOutlined } from '@mui/icons-material';
import styles from './styles.module.css';
import { SearchType } from 'types/loctions';
import { useInsertOptionManufacturer } from 'store/manufacture';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { archiveSubmittedEntries } from 'services/asset';
import { useFlash } from 'contexts/flash';
import { renderDataAssetValidation } from './RenderDataAssetValidation';
import { useFilteredRoomContext } from 'hooks/useFilteredRoomContext';
import { Image } from 'app/components/elements/Image';

export type AssetValidationTabProps = {
  pagingInfo: PagingInfo;
  handleChangePage: (value: number) => void;
  handleSort: (orderBy: string) => void;
  sortOption: { field?: string; order?: string };
  onSearch?: (value: string) => void;
  initSearchText?: string;
  refreshDataValidation: () => AssetIssues[];
  dataAssetValidation: AssetIssues[];
  setDataAssetValidation: (value: AssetIssues[]) => void;
  handleSelectGenerate: (
    type: string,
    imageOption: string,
    columns?: string[],
    propertiesColumns?: string[],
    dropdownOptionColumns?: number[],
    singleFieldColumns?: number[]
  ) => void;
  dateValidationAsset: { startDate?: string; endDate?: string };
  setDateValidationAsset: (value: { startDate?: string; endDate?: string }) => void;
  onEditSubmittedEntries: (valueSubmit: {
    value: FormEditSubmittedEntries;
    rowActive: AssetIssues;
  }) => void;
  filterValidations?: { key: string; value: string; label: string; level?: number }[];
  setFilterValidations: (value?: {
    key: string;
    value: string;
    label: string;
    level?: number;
  }) => void;
  options: ListOptions;
  onFilterCriticality: (criticalityValue: SelectChangeEvent<unknown>) => void;
  selectedCriticality: string;
  maintainanceSelectedDate?: string;
  maintainanceSelectedType: string;
  setSelectedMaintainanceDate: (value: string) => void;
  setSelectedMaintainanceType: (value: string) => void;
  searchType: SearchType;
  setSearchType: React.Dispatch<React.SetStateAction<SearchType>>;
  expandedSubmittedAssetId?: number;
  onExpandedSubmittedAssetChange: (submittedAssetId: number | undefined) => void;
};
export const AssetValidationTab = ({
  pagingInfo,
  handleChangePage,
  handleSort,
  sortOption,
  onSearch,
  dataAssetValidation,
  initSearchText,
  onEditSubmittedEntries,
  filterValidations,
  setFilterValidations,
  setDataAssetValidation,
  dateValidationAsset,
  handleSelectGenerate,
  setDateValidationAsset,
  options,
  refreshDataValidation,
  onFilterCriticality,
  selectedCriticality,
  maintainanceSelectedType,
  maintainanceSelectedDate,
  setSelectedMaintainanceDate,
  setSelectedMaintainanceType,
  searchType,
  setSearchType,
  expandedSubmittedAssetId,
  onExpandedSubmittedAssetChange
}: AssetValidationTabProps) => {
  const { systemDropdownValues } = useFilteredRoomContext();
  const navigate = useNavigate();
  const [dateSelected, setDateSelected] = useState<{ startDate?: string; endDate?: string }>({
    startDate: '',
    endDate: ''
  });

  const [exportModalOpen, setExportModalOpen] = useState(false);

  const insertOptionManufacturer = useInsertOptionManufacturer();

  useEffect(() => {
    setDateSelected(dateValidationAsset);
  }, [dateValidationAsset]);

  const {
    searchText,
    setSearchText,
    rowActive,
    idActiveRow,
    setPreviewGenerate,
    room,
    previewGenerate,
    generateReport,
    setGenerateReport,
    optionIssues,
    handleFetchMoreIssues,
    hasMoreIssues,
    optionAssetSelect,
    onCloseModalGenerateReport,
    onSubmitModalGenerateReport,
    handleUpdateAsset,
    asyncAddDocument,
    documentOptions,
    documentGenerate,
    modelFiles,
    selectedCriticalityOptions,
    onGenerateSingleAsset
  } = useInjection({
    initSearchText,
    setDataAssetValidation,
    dataAssetValidation,
    refreshDataValidation,
    expandedSubmittedAssetId,
    onExpandedSubmittedAssetChange
  });

  const issuesOptions = useMemo(() => {
    const assetIssuesOptions: Record<string, string> = {};
    rowActive?.options?.forEach((assetOption) => {
      if (assetOption.fieldType === 'DROPDOWN') {
        systemDropdownValues?.forEach((dropdownValue) => {
          const hasAssetType = dropdownValue.id.toString() === assetOption.type;

          if (hasAssetType) {
            const index = dropdownValue.dropdownValues.some(
              (dropdown) => dropdown.value === assetOption.value
            );

            assetIssuesOptions[`option_${assetOption?.type}`] = index
              ? assetOption?.value
              : 'OTHER';
            assetIssuesOptions[`option_${assetOption?.type}_name`] = assetOption?.value;
          }
        });
      } else {
        assetIssuesOptions[`single_${assetOption?.type}`] = assetOption?.value;
      }
    });
    return assetIssuesOptions;
  }, [rowActive?.options, systemDropdownValues]);

  const { setFlash } = useFlash();

  const { asyncCallback: onArchive } = useAsyncCallback(async (rowActive: AssetIssues) => {
    await archiveSubmittedEntries({
      submitId: rowActive.submitId,
      id: rowActive.id,
      archive: !rowActive.archivedAt
    });

    refreshDataValidation();
    setFlash({ type: 'success', message: 'Archive successful' });
  }, []);

  const { auth } = useAuth();
  const isCanAction = useMemo(
    () => (auth?.role ? [Role.ADMIN, Role.SUPER_ADMIN].includes(auth?.role) : false),
    [auth?.role]
  );

  const mapOptions = useMemo(() => {
    const mapOptions = new Map();
    options?.forEach((op) => {
      mapOptions.set(op.id.toString(), op);
    });
    return mapOptions;
  }, [options]);

  const optionRoomLevelIssues = useMemo(() => {
    if (!room) return [];
    return (
      room.roomLevelIssues
        .filter((item) => !item.archivedAt)
        .map((item) => ({
          value: item.id.toString(),
          label: item.options?.[0]?.value
        })) || []
    );
  }, [room]);

  const tableRoomLevelIssueHeaders = useMemo(() => {
    const tableRoomLevelIssueHeaders = [...ROOM_LEVEL_ISSUES];
    let roomLevelIssuesLargestOptions: IOptions[] = [];
    room?.roomLevelIssues?.forEach((issue) => {
      roomLevelIssuesLargestOptions = [...roomLevelIssuesLargestOptions, ...issue.options];
      roomLevelIssuesLargestOptions = uniqBy(roomLevelIssuesLargestOptions, 'type');
    });
    roomLevelIssuesLargestOptions?.forEach((reportOption, index) => {
      const fieldKey = mapOptions.get(reportOption.type)?.name || reportOption?.type;
      tableRoomLevelIssueHeaders.splice(0 + index, 0, {
        label: startCase(lowerCase(fieldKey)),
        key: fieldKey,
        width: 200
      });
    });
    return { headers: tableRoomLevelIssueHeaders, roomLevelIssuesLargestOptions };
  }, [room?.roomLevelIssues, mapOptions]);

  const urlFilter = useMemo(() => {
    if (!room)
      return { outside: { url: '', type: '' }, internal: { url: '', type: '' }, walls: [] };

    const wallsMap: Record<string, SubmittedPhoto[]> = {};

    const result = room.submittedPhotos.reduce(
      (acc, photo) => {
        switch (photo.type) {
          case TypePhotoSubmit.OUTSIDE:
            acc.outside = { url: photo.url, type: photo.storage.type };
            break;
          case TypePhotoSubmit.INTERNAL:
            acc.internal = { url: photo.url, type: photo.storage.type };
            break;
          default:
            if (photo.type.startsWith('WALL')) {
              const name = photo.type.toLowerCase();
              if (!wallsMap[name]) {
                wallsMap[name] = [];
              }
              wallsMap[name].push(photo);
            }
            break;
        }
        return acc;
      },
      {
        outside: { url: '', type: '' },
        internal: { url: '', type: '' },
        walls: [] as { name: string; photos: SubmittedPhoto[] }[]
      }
    );

    // Convert wallsMap to walls array
    result.walls = Object.entries(wallsMap).map(([name, photos]) => ({
      name,
      photos
    }));

    return result;
  }, [room]);
  const renderData = useCallback(() => {
    return (
      <ContentExtraTableValidation
        onEditSubmittedEntries={() => {}}
        dataRowActive={previewGenerate}
        mapOptions={mapOptions}
      />
    );
  }, [previewGenerate, mapOptions]);
  return (
    <WrapperTab>
      <Stack flexDirection={'row'} flexWrap="wrap" justifyContent={'space-between'} rowGap="10px">
        <Stack flexDirection={'row'} flexWrap="wrap" columnGap={10} rowGap="10px">
          <Stack>
            <Typography
              style={{ fontSize: 18, fontWeight: '700', color: '#226AAA', marginBottom: 10 }}>
              Search by keyword
            </Typography>
            {onSearch && (
              <HeaderValidation
                searchText={searchText}
                setSearchText={setSearchText}
                onSearch={() => onSearch(searchText)}
              />
            )}
          </Stack>
          <Stack>
            <Typography style={{ fontSize: 18, fontWeight: '700', color: '#226AAA' }}>
              Criticality
            </Typography>
            <InputSelect
              value={selectedCriticality}
              onChange={onFilterCriticality}
              options={selectedCriticalityOptions}
              placeholder={`Select Criticality`}
            />
          </Stack>
          <Stack>
            <Typography style={{ fontSize: 18, fontWeight: '700', color: '#226AAA' }}>
              Filter Action By Date
            </Typography>
            <Stack flexDirection={'row'} gap={2} paddingTop={'10px'} flexWrap="wrap">
              <Stack flexDirection={'row'} flexWrap="wrap" gap={2}>
                <Stack
                  sx={{ marginRight: '10px', position: 'relative', minWidth: 220, width: 220 }}>
                  <DatePicker
                    maxDate={dateSelected?.endDate ? dayjs(dateSelected?.endDate) : undefined}
                    value={dateSelected?.startDate ? dayjs(dateSelected.startDate) : null}
                    onChange={(val) =>
                      setDateSelected({
                        endDate: dateSelected.endDate,
                        startDate: val && val.isValid() ? val.toISOString() : undefined
                      })
                    }
                    title={'Start Date'}
                  />
                  {dateSelected?.startDate && (
                    <CloseOutlined
                      className={styles.reset_date}
                      style={{ marginBottom: 56 / 2 - 10 }}
                      onClick={() => {
                        setDateSelected({
                          endDate: dateSelected.endDate,
                          startDate: undefined
                        });
                      }}
                    />
                  )}
                </Stack>

                <Stack sx={{ position: 'relative', minWidth: 220, width: 220 }}>
                  <DatePicker
                    minDate={dateSelected?.startDate ? dayjs(dateSelected?.startDate) : undefined}
                    value={dateSelected?.endDate ? dayjs(dateSelected?.endDate) : null}
                    onChange={(val) =>
                      setDateSelected({
                        startDate: dateSelected.startDate,
                        endDate: val && val.isValid() ? val.toISOString() : undefined
                      })
                    }
                    title={'End Date'}
                  />
                  {dateSelected?.endDate && (
                    <CloseOutlined
                      className={styles.reset_date}
                      style={{ marginBottom: 56 / 2 - 10 }}
                      onClick={() => {
                        setDateSelected({
                          startDate: dateSelected.startDate,
                          endDate: undefined
                        });
                      }}
                    />
                  )}
                </Stack>
              </Stack>
              <Stack
                alignItems={'flex-end'}
                justifyContent={'flex-end'}
                flexDirection={'row'}
                gap={'10px'}>
                <ButtonCustom
                  style={{ width: '220px' }}
                  label={'Find'}
                  disabled={
                    (dateSelected.startDate !== '' && !dayjs(dateSelected.startDate).isValid()) ||
                    (dateSelected.endDate !== '' && !dayjs(dateSelected.endDate).isValid())
                  }
                  onClick={() => {
                    setDateValidationAsset(dateSelected);
                  }}
                />
                <ButtonCustom label={'Export'} onClick={() => setExportModalOpen(true)} />
              </Stack>
            </Stack>
          </Stack>
        </Stack>

        <ExportColumnModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          onExport={handleSelectGenerate}
        />
        <MaintainenceFilter
          options={options}
          maintainanceSelectedType={maintainanceSelectedType}
          maintainanceSelectedDate={maintainanceSelectedDate}
          setMaintenanceSelectedDate={setSelectedMaintainanceDate}
          setSelectedMaintainenceType={setSelectedMaintainanceType}
          searchType={searchType}
          setSearchType={setSearchType}
          inputSelectStyle={{ minHeight: '46px', height: '46px', width: '100%' }}
        />
      </Stack>

      <Stack sx={{ marginTop: '30px', marginBottom: '30px' }}>
        {dataAssetValidation && (
          <TableCustom
            infoStickies={[{ index: 0, leftSpace: 0, isBorder: true }]}
            isScrollX
            onSortHeader={handleSort}
            sort={sortOption}
            data={renderDataAssetValidation({
              onPreview: setPreviewGenerate,
              onGenerateReport: setGenerateReport,
              onGenerateSingleAsset: onGenerateSingleAsset,
              onCreateNewIssue: (value) => {
                navigate(
                  `/clients/${value.room.client.clientName}/${value.room.client.id}/location/${value.room.id}/room/issues/create-issue?room-name=${encodeURIComponent(value.room.name)}&equipment-id=${encodeURIComponent(value.name)}&asset-id=${value.id}&submitted-id=${value.submitId}`
                );
              },
              keySearch: filterValidations,
              dataAssetValidation: dataAssetValidation,
              indexActive: idActiveRow,
              onArchive,
              onClickRoom: (value?: {
                key: string;
                value: string;
                label: string;
                level?: number;
              }) => {
                setFilterValidations(value);
              },
              onCollapse: ({ value, indexRow }) => {
                const collapsing = indexRow === idActiveRow;
                if (collapsing) {
                  onExpandedSubmittedAssetChange(undefined);
                  return;
                }
                if (value?.model?.manufacturer?.id)
                  insertOptionManufacturer({
                    label: value?.model?.manufacturer.name,
                    value: value?.model?.manufacturer?.id?.toString()
                  });
                onExpandedSubmittedAssetChange(value.id);
              },
              onViewIssues: (value) => {
                navigate(
                  `/clients/${value.room.client.clientName}/${value.room.client.id}/location/${value.room.id}/room/issues/${value.issueReports[0].id}/view-photo`
                );
              },
              isCanAction
            })}
            contentExtra={
              <WrapperItemExtra sx={{ width: '100%', maxWidth: '100%', alignItems: 'flex-start' }}>
                <AssetValidationExpandedContent
                  rowActive={rowActive}
                  mapOptions={mapOptions}
                  options={options}
                  documentOptions={documentOptions}
                  modelFiles={modelFiles}
                  issuesOptions={issuesOptions}
                  onEditSubmittedEntries={onEditSubmittedEntries}
                  onSubmitAsset={handleUpdateAsset}
                  asyncAddDocument={asyncAddDocument}
                />
              </WrapperItemExtra>
            }
            headers={HEADER_ASSET_VALIDATION_TAB}
            handleChooseRow={() => {}}
            indexRowShowExtra={idActiveRow}
            styledTableRow={{ cursor: 'default' }}
            pagingInfo={pagingInfo}
            handleChangePage={handleChangePage}
          />
        )}
      </Stack>
      <DialogConfirm
        maxWidth={1200}
        isOpen={!!previewGenerate}
        onClose={() => setPreviewGenerate(undefined)}>
        <Stack
          sx={{
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            backgroundColor: '#fff',
            outline: 'none',
            padding: '20px 50px',
            gap: 2.5
          }}>
          <Typography fontSize={'18px'} fontWeight={700} color={'#226AAA'}>
            {`${previewGenerate?.room.name}`}
          </Typography>
          <Stack flexDirection={'row'} gap={'20px'}>
            <Image url={urlFilter.outside} title={'Outside Photo'} />
            <Image url={urlFilter.internal} title={'Internal Panoramic Photo'} />
          </Stack>
          <WrapperFilter maxWidth={'100%'}>
            <TextTitle>Location Level Issues</TextTitle>
            {room?.roomLevelIssues && room?.roomLevelIssues.length > 0 && (
              <TableCustom
                data={renderRoomLevelIssues({
                  dataTable: room?.roomLevelIssues,
                  getLabelField: (type) => mapOptions.get(type)?.name || type,
                  optionsLargestInTable: tableRoomLevelIssueHeaders.roomLevelIssuesLargestOptions,
                  isCanAction
                })}
                handleChooseRow={() => {}}
                headers={tableRoomLevelIssueHeaders.headers}
                styledTableRow={{ cursor: 'default' }}
                isScrollX
              />
            )}
          </WrapperFilter>
          {urlFilter?.walls?.map((item, index) => {
            return (
              <Stack key={index} width={'100%'} minHeight={'30px'} gap={1.25} maxWidth={'1120px'}>
                <TextTitle>{formatName(item.name)}</TextTitle>
                <Stack
                  gap={2.5}
                  flexDirection={'row'}
                  justifyContent="flex-start"
                  flexWrap={'wrap'}
                  alignItems="flex-start">
                  {item.photos.map((photo, photoIndex) => {
                    return (
                      <Image key={photoIndex} url={{ url: photo.url, type: photo.storage.type }} />
                    );
                  })}
                </Stack>
              </Stack>
            );
          })}
          {room?.additionalStructures.map((item, index) => (
            <Stack width={'100%'} minHeight={'30px'} gap={1.25} key={index} maxWidth={'1120px'}>
              <TextTitle>{item.type === 'other' ? item.name : item.type}</TextTitle>
              <Stack
                flexDirection={'row'}
                gap={2.5}
                justifyContent="flex-start"
                flexWrap={'wrap'}
                alignItems="flex-start">
                {item.additionalStructurePhotos.map((itemPhoto, index) => (
                  <Image key={index} url={{ url: itemPhoto.url, type: itemPhoto.photo.type }} />
                ))}
              </Stack>
            </Stack>
          ))}
          <Stack width={'1000px'}>
            {previewGenerate && (
              <TableCustom
                isScrollX
                onSortHeader={handleSort}
                sort={sortOption}
                data={renderDataComplianceIssue({
                  readOnly: true,
                  dataTable: [previewGenerate],
                  indexActive: idActiveRow,
                  onCollapse: ({ value, indexRow }) => {
                    const collapsing = indexRow === idActiveRow;
                    if (collapsing) {
                      onExpandedSubmittedAssetChange(undefined);
                      return;
                    }
                    onExpandedSubmittedAssetChange(value.id);
                  },
                  onViewIssues: (value) => {
                    console.log(value);
                  },
                  onCreateNewIssue: (value) => {
                    navigate(
                      `/clients/${value.room.client.clientName}/${value.room.client.id}/location/${value.room.id}/room/issues/create-issue?room-name=${encodeURIComponent(value.room.name)}&equipment-id=${encodeURIComponent(value.name)}&asset-id=${value.id}&submitted-id=${value.submitId}`
                    );
                  }
                })}
                contentExtra={renderData()}
                headers={HEADER_REPORT_PREVIEW}
                handleChooseRow={() => {}}
                indexRowShowExtra={idActiveRow}
                styledTableRow={{ cursor: 'default' }}
                handleChangePage={handleChangePage}
              />
            )}
          </Stack>
        </Stack>
      </DialogConfirm>
      <ModalGenerateReport
        documentGenerate={documentGenerate}
        optionRoomLevelIssues={optionRoomLevelIssues}
        optionIssues={optionIssues}
        handleFetchMoreIssues={handleFetchMoreIssues}
        hasMoreIssues={hasMoreIssues}
        optionAsset={
          optionAssetSelect?.map((item: AssetIssues) => ({
            label: `${item?.name} - ${item?.type?.name} - ${item?.issueReports?.length ? `${item?.issueReports?.length} issues` : 'no issues'}`,
            value: item.id.toString()
          })) || []
        }
        onCloseModal={onCloseModalGenerateReport}
        onCancel={onCloseModalGenerateReport}
        onSubmit={onSubmitModalGenerateReport}
        isOpen={!!generateReport}
      />
    </WrapperTab>
  );
};

export const MemoAssetValidationTab = memo(AssetValidationTab);

const WrapperTab = styled(Stack)(() => ({
  width: '100%',
  marginTop: 20
}));
const WrapperItemExtra = styled(Stack)(() => ({
  borderRadius: 10,
  border: '1px solid #E0E0E0',
  padding: '20px'
}));
const WrapperFilter = styled(Stack)(() => ({
  width: '100%',
  gap: '10px'
}));
const TextTitle = styled(Typography)(() => ({
  color: '#226AAA',
  fontSize: '18px',
  fontWeight: 900
}));
